/**
 * Time Series Data Cleaning Functions
 * 
 * Handles common data quality issues in MWD time series data:
 * - Timestamp outliers (e.g., 2025 data with random 2023 points)
 * - Unsorted data
 * - Duplicate timestamps
 * - Invalid/NaN values
 */

import type { Curve } from '../../interfaces/toolData';

export interface TimeSeriesCleanupOptions {
  // Simple cleaning options - currently no user-configurable options needed
  // Always: sort timestamps, remove pre-2010/post-today, remove timestamp outliers > 3σ, remove invalid Y values
}

export interface TimeSeriesCleanupResult {
  /** Cleaned curve with same metadata but cleaned data */
  cleanedCurve: Curve;
  /** Indices of original data points that were kept */
  keptIndices: number[];
  /** Summary of cleaning operations performed */
  cleanupSummary: {
    originalCount: number;
    finalCount: number;
    removedCount: number;
    timeOutliersRemoved: number;
    yValueOutliersRemoved: number;
    duplicatesRemoved: number;
    invalidYValuesRemoved: number;
    dataRetentionRate: number;
  };
  /** Warnings about data quality issues */
  warnings: string[];
}

/**
 * Clean a curve by removing outliers, sorting, and deduplicating
 */
export function cleanTimeSeriesData(
  curve: Curve,
  _options: TimeSeriesCleanupOptions = {}
): TimeSeriesCleanupResult {
  // const _opts = {
  //   ...options
  // };

  const warnings: string[] = [];
  const originalCount = Math.min(curve.xValues.length, curve.yValues.length);
  


  if (originalCount === 0) {
    return {
      cleanedCurve: { ...curve },
      keptIndices: [],
      cleanupSummary: {
        originalCount: 0,
        finalCount: 0,
        removedCount: 0,
        timeOutliersRemoved: 0,
        yValueOutliersRemoved: 0,
        duplicatesRemoved: 0,
        invalidYValuesRemoved: 0,
        dataRetentionRate: 0
      },
      warnings: ['No data provided for cleaning']
    };
  }

  // xValues should already be Date objects per interface
  const timestamps: Date[] = curve.xValues.slice(0, originalCount);

  // Truncate Y values to match X values length  
  const yVals = curve.yValues.slice(0, originalCount);

  // Create array of indices for tracking
  let dataPoints = Array.from({ length: originalCount }, (_, i) => ({
    x: timestamps[i],
    y: yVals[i],
    originalIndex: i
  }));

  let timeOutliersRemoved = 0;
  const yValueOutliersRemoved = 0;
  const duplicatesRemoved = 0;
  let invalidYValuesRemoved = 0;

  // Step 1: Sort by timestamp
  dataPoints.sort((a, b) => a.x.getTime() - b.x.getTime());

  // Step 2: Remove timestamps before 2010 and after today
  const minDate = new Date('2010-01-01T00:00:00Z');
  const maxDate = new Date();
  let beforeTimeFilter = dataPoints.length;
  dataPoints = dataPoints.filter(point => {
    const timestamp = point.x.getTime();
    return !isNaN(timestamp) && point.x >= minDate && point.x <= maxDate;
  });
  const dateRangeRemoved = beforeTimeFilter - dataPoints.length;
  if (dateRangeRemoved > 0) {
    warnings.push(`Removed ${dateRangeRemoved} timestamps outside 2010-today range`);
  }

  // Step 3: Remove timestamp outliers using 3 standard deviations
  if (dataPoints.length > 3) {
    const timestamps = dataPoints.map(p => p.x.getTime());
    const mean = timestamps.reduce((sum, val) => sum + val, 0) / timestamps.length;
    const variance = timestamps.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / timestamps.length;
    const stdDev = Math.sqrt(variance);
    const threshold = 3 * stdDev;
    
    beforeTimeFilter = dataPoints.length;
    dataPoints = dataPoints.filter(point => 
      Math.abs(point.x.getTime() - mean) <= threshold
    );
    const statisticalOutliers = beforeTimeFilter - dataPoints.length;
    timeOutliersRemoved = dateRangeRemoved + statisticalOutliers;
    if (statisticalOutliers > 0) {
      warnings.push(`Removed ${statisticalOutliers} timestamp outliers beyond 3σ`);
    }
  } else {
    timeOutliersRemoved = dateRangeRemoved;
  }

  // Step 4: Remove invalid Y values
  const beforeInvalidY = dataPoints.length;
  dataPoints = dataPoints.filter(point => 
    typeof point.y === 'number' && isFinite(point.y) && !isNaN(point.y)
  );
  invalidYValuesRemoved = beforeInvalidY - dataPoints.length;
  if (invalidYValuesRemoved > 0) {
    warnings.push(`Removed ${invalidYValuesRemoved} invalid Y values`);
  }

  // Step 5: Y value outlier detection removed per user request

  // Check minimum data retention rate
  const finalCount = dataPoints.length;
  const dataRetentionRate = originalCount > 0 ? finalCount / originalCount : 0;

  // Create cleaned curve preserving original metadata
  const cleanedCurve: Curve = {
    ...curve,
    xValues: dataPoints.map(p => p.x),
    yValues: dataPoints.map(p => p.y)
  };

  return {
    cleanedCurve,
    keptIndices: dataPoints.map(p => p.originalIndex),
    cleanupSummary: {
      originalCount,
      finalCount,
      removedCount: originalCount - finalCount,
      timeOutliersRemoved,
      yValueOutliersRemoved,
      duplicatesRemoved,
      invalidYValuesRemoved,
      dataRetentionRate
    },
    warnings
  };
} 